import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CrmCustomFieldsDefinitionsService,
  CustomFieldDefinition,
  CustomFieldDefinitionGetResponse,
  CustomFieldDefinitionListQueryParams,
  CustomFieldDefinitionListResponse,
  CustomFieldDefinitionCreateBodyParams,
  CustomFieldDefinitionUpdateBodyParams,
  CustomFieldEntityType,
} from 'src/api/generated';
import { ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { MINUTE_IN_MS } from 'src/utils/date';

interface UseCustomFieldDefinitionsParams
  extends CustomFieldDefinitionListQueryParams {
  entityType?: CustomFieldEntityType;
  ids?: number[];
  enabled?: boolean;
}

interface UseCustomFieldDefinitionsResponse extends ApiResponse {
  definitions: CustomFieldDefinition[];
}

/**
 * Hook for fetching custom field definitions
 * Optionally filter by entity type
 */
export const useCustomFieldDefinitions = (
  params: UseCustomFieldDefinitionsParams = {}
): UseCustomFieldDefinitionsResponse => {
  const {
    entityType,
    ids,
    enabled = true,
    pageSize = 100,
    ...queryParams
  } = params;
  const queryBuilder = createQuery().withExisting({
    filterBy: queryParams.filterBy,
    sortBy: queryParams.sortBy,
    searchBy: queryParams.searchBy,
    page: queryParams.page,
    pageSize,
    pageToken: queryParams.pageToken,
  });

  if (entityType) {
    queryBuilder.equals('entityType', entityType).orderBy('createdAt', 'asc');
  }

  if (ids && ids.length > 0) {
    queryBuilder.equals('id', ids);
  }

  const query = queryBuilder.build();

  const fetchDefinitions =
    async (): Promise<CustomFieldDefinitionListResponse> => {
      return await CrmCustomFieldsDefinitionsService.getCustomFieldDefinitions(
        query.filterBy,
        query.searchBy,
        query.sortBy,
        query.page,
        query.pageSize,
        query.pageToken
      );
    };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/custom-fields/definitions', { ...params }],
    queryFn: fetchDefinitions,
    enabled,
    staleTime: MINUTE_IN_MS * 10,
  });

  const definitions = useMemoizedArrayData<CustomFieldDefinition>(
    data?.items || []
  );

  return {
    loading,
    error,
    definitions,
    refetch,
  };
};

interface UseCustomFieldDefinitionResponse extends ApiResponse {
  definition: CustomFieldDefinition | null;
}

/**
 * Hook for fetching a single custom field definition by ID
 */
export const useCustomFieldDefinition = (
  id: number,
  enabled: boolean = true
): UseCustomFieldDefinitionResponse => {
  const fetchDefinition =
    async (): Promise<CustomFieldDefinitionGetResponse> => {
      return await CrmCustomFieldsDefinitionsService.getCustomFieldDefinition(
        id
      );
    };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/custom-fields/definitions', id],
    queryFn: fetchDefinition,
    enabled,
  });

  return {
    loading,
    error,
    definition: data || null,
    refetch,
  };
};

// Mutation hooks for CRUD operations

interface UseCreateCustomFieldDefinitionParams {
  onSuccess?: (definition: CustomFieldDefinition) => void;
  onError?: (error: Error) => void;
}

interface UseCreateCustomFieldDefinitionResponse {
  createDefinition: (
    data: CustomFieldDefinitionCreateBodyParams
  ) => Promise<CustomFieldDefinition>;
  loading: boolean;
  error: Error | null;
}

export const useCreateCustomFieldDefinition = (
  params: UseCreateCustomFieldDefinitionParams = {}
): UseCreateCustomFieldDefinitionResponse => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const createDefinitionMutation = async (
    data: CustomFieldDefinitionCreateBodyParams
  ): Promise<CustomFieldDefinition> => {
    return await CrmCustomFieldsDefinitionsService.createCustomFieldDefinition(
      data
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CustomFieldDefinition,
    Error,
    CustomFieldDefinitionCreateBodyParams
  >({
    mutationFn: createDefinitionMutation,
    onSuccess: (definition) => {
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/definitions'],
      });
      onSuccess?.(definition);
    },
    onError,
  });

  return {
    createDefinition: mutateAsync,
    loading,
    error: error || null,
  };
};

interface UseUpdateCustomFieldDefinitionParams {
  onSuccess?: (definition: CustomFieldDefinition) => void;
  onError?: (error: Error) => void;
}

interface UpdateCustomFieldDefinitionData {
  id: number;
  data: CustomFieldDefinitionUpdateBodyParams;
}

interface UseUpdateCustomFieldDefinitionResponse {
  updateDefinition: (
    data: UpdateCustomFieldDefinitionData
  ) => Promise<CustomFieldDefinition>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateCustomFieldDefinition = (
  params: UseUpdateCustomFieldDefinitionParams = {}
): UseUpdateCustomFieldDefinitionResponse => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const updateDefinitionMutation = async (
    data: UpdateCustomFieldDefinitionData
  ): Promise<CustomFieldDefinition> => {
    const { id, data: updateData } = data;
    return await CrmCustomFieldsDefinitionsService.updateCustomFieldDefinition(
      id,
      updateData
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CustomFieldDefinition,
    Error,
    UpdateCustomFieldDefinitionData
  >({
    mutationFn: updateDefinitionMutation,
    onSuccess: (definition) => {
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/definitions'],
      });
      onSuccess?.(definition);
    },
    onError,
  });

  return {
    updateDefinition: mutateAsync,
    loading,
    error: error || null,
  };
};

interface UseDeleteCustomFieldDefinitionParams {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface DeleteCustomFieldDefinitionData {
  id: number;
}

interface UseDeleteCustomFieldDefinitionResponse {
  deleteDefinition: (data: DeleteCustomFieldDefinitionData) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

export const useDeleteCustomFieldDefinition = (
  params: UseDeleteCustomFieldDefinitionParams = {}
): UseDeleteCustomFieldDefinitionResponse => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const deleteDefinitionMutation = async (
    data: DeleteCustomFieldDefinitionData
  ): Promise<void> => {
    const { id } = data;
    await CrmCustomFieldsDefinitionsService.deleteCustomFieldDefinition(id);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<void, Error, DeleteCustomFieldDefinitionData>({
    mutationFn: deleteDefinitionMutation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/definitions'],
      });
      onSuccess?.();
    },
    onError,
  });

  return {
    deleteDefinition: mutateAsync,
    loading,
    error: error || null,
  };
};
